import { Container, Row, Col, Card } from 'react-bootstrap';
import { 
  Building2, 
  GraduationCap, 
  Settings, 
  Users,
  Shield,
  Lock,
  Server,
  HeadphonesIcon,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { PageBanner } from '../components/layout/PageBanner';

const GovEducation = () => {
  const { t, language } = useLanguage();

  const solutions = [
    {
      icon: Building2,
      title: t('govEdu.govTitle'),
      description: t('govEdu.govDesc'),
      features: language === 'hi'
        ? ['ई-गवर्नेंस पोर्टल', 'डिजिटल दस्तावेज़ प्रबंधन', 'नागरिक सेवा अनुप्रयोग', 'डेटा एनालिटिक्स']
        : ['E-Governance Portals', 'Digital Document Management', 'Citizen Service Apps', 'Data Analytics']
    },
    {
      icon: GraduationCap,
      title: t('govEdu.digitalTitle'),
      description: t('govEdu.digitalDesc'),
      features: language === 'hi'
        ? ['लर्निंग मैनेजमेंट सिस्टम', 'ऑनलाइन परीक्षा प्लेटफॉर्म', 'छात्र पोर्टल', 'डिजिटल लाइब्रेरी']
        : ['Learning Management Systems', 'Online Exam Platforms', 'Student Portals', 'Digital Libraries']
    },
    {
      icon: Settings,
      title: t('govEdu.erpTitle'),
      description: t('govEdu.erpDesc'),
      features: language === 'hi'
        ? ['प्रवेश प्रबंधन', 'फीस प्रबंधन', 'उपस्थिति ट्रैकिंग', 'परिणाम प्रबंधन']
        : ['Admission Management', 'Fee Management', 'Attendance Tracking', 'Result Management']
    },
    {
      icon: Users,
      title: t('govEdu.skillTitle'),
      description: t('govEdu.skillDesc'),
      features: language === 'hi'
        ? ['फैकल्टी डेवलपमेंट', 'छात्र प्रशिक्षण', 'ऑनलाइन कोर्स', 'कार्यशालाएं']
        : ['Faculty Development', 'Student Training', 'Online Courses', 'Workshops']
    }
  ];

  const keyFeaturesIcons = { Shield, Lock, Server, HeadphonesIcon };
  const keyFeatures = [
    { iconName: 'Shield', text: t('govEdu.feature1') },
    { iconName: 'Lock', text: t('govEdu.feature2') },
    { iconName: 'Server', text: t('govEdu.feature3') },
    { iconName: 'HeadphonesIcon', text: t('govEdu.feature4') }
  ];

  return (
    <main id="main-content">
      <PageBanner 
        title={t('govEdu.pageTitle')} 
        breadcrumb={t('govEdu.breadcrumb')} 
      />

      {/* Introduction */}
      <section className="gov-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <p style={{ 
                fontSize: '1.125rem', 
                color: 'hsl(var(--muted-foreground))',
                textAlign: 'center',
                lineHeight: 1.7
              }}>
                {t('govEdu.intro')}
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Key Features Banner */}
      <section style={{ paddingBottom: '3rem' }}>
        <Container>
          <div 
            style={{
              background: 'var(--gradient-gov-banner)',
              borderRadius: 'var(--radius-lg)',
              padding: '2rem',
              color: 'white'
            }}
          >
            <h3 style={{ 
              fontSize: '1.125rem', 
              fontWeight: '600', 
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              {t('govEdu.features')}
            </h3>
            <Row className="g-3">
              {keyFeatures.map((item, index) => {
                const IconComponent = keyFeaturesIcons[item.iconName];
                return (
                  <Col key={index} sm={6} lg={3}>
                    <div 
                      className="d-flex align-items-center gap-3 p-3"
                      style={{
                        background: 'rgba(255,255,255,0.1)',
                        borderRadius: 'var(--radius)'
                      }}
                    >
                      <IconComponent size={24} />
                      <span style={{ fontSize: '0.9375rem' }}>{item.text}</span>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </div>
        </Container>
      </section>

      {/* Solutions Grid */}
      <section className="gov-section gov-section-alt" style={{ paddingTop: 0 }}>
        <Container>
          <Row className="g-4">
            {solutions.map((solution, index) => (
              <Col key={index} md={6}>
                <Card className="gov-card h-100">
                  <Card.Body className="p-4">
                    <div 
                      style={{
                        width: '56px',
                        height: '56px',
                        background: 'var(--gradient-accent-subtle)',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '1.25rem'
                      }}
                    >
                      <solution.icon size={28} style={{ color: 'hsl(var(--primary))' }} />
                    </div>

                    <h3 style={{ 
                      fontSize: '1.125rem', 
                      fontWeight: '600', 
                      marginBottom: '0.75rem',
                      color: 'hsl(var(--foreground))'
                    }}>
                      {solution.title}
                    </h3>

                    <p style={{ 
                      fontSize: '0.9375rem', 
                      color: 'hsl(var(--muted-foreground))',
                      marginBottom: '1.25rem',
                      lineHeight: 1.6
                    }}>
                      {solution.description}
                    </p>

                    <ul style={{ listStyle: 'none', padding: 0, marginBottom: 0 }}>
                      {solution.features.map((feature, fIndex) => (
                        <li 
                          key={fIndex}
                          className="d-flex align-items-center gap-2 mb-2"
                          style={{ fontSize: '0.875rem' }}
                        >
                          <CheckCircle 
                            size={14} 
                            style={{ color: 'hsl(var(--success))', flexShrink: 0 }} 
                          />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Target Sectors */}
      <section className="gov-section">
        <Container>
          <div className="text-center mb-5">
            <h2 className="gov-section-heading">
              {language === 'hi' ? 'लक्षित क्षेत्र' : 'Target Sectors'}
            </h2>
          </div>

          <Row className="g-4">
            {[
              {
                title: language === 'hi' ? 'केंद्रीय और राज्य सरकार' : 'Central & State Government',
                items: language === 'hi' 
                  ? ['मंत्रालय और विभाग', 'जिला प्रशासन', 'नगर निकाय']
                  : ['Ministries & Departments', 'District Administration', 'Municipal Bodies']
              },
              {
                title: language === 'hi' ? 'शैक्षणिक संस्थान' : 'Educational Institutions',
                items: language === 'hi' 
                  ? ['विश्वविद्यालय', 'कॉलेज', 'स्कूल']
                  : ['Universities', 'Colleges', 'Schools']
              },
              {
                title: language === 'hi' ? 'सार्वजनिक क्षेत्र' : 'Public Sector',
                items: language === 'hi' 
                  ? ['PSUs', 'स्वायत्त निकाय', 'वैधानिक निकाय']
                  : ['PSUs', 'Autonomous Bodies', 'Statutory Bodies']
              }
            ].map((sector, index) => (
              <Col key={index} md={4}>
                <div 
                  style={{
                    background: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 'var(--radius-lg)',
                    padding: '1.5rem',
                    height: '100%',
                    borderTop: '4px solid hsl(var(--primary))'
                  }}
                >
                  <h4 style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    marginBottom: '1rem',
                    color: 'hsl(var(--primary))'
                  }}>
                    {sector.title}
                  </h4>
                  <ul style={{ listStyle: 'none', padding: 0, marginBottom: 0 }}>
                    {sector.items.map((item, iIndex) => (
                      <li 
                        key={iIndex}
                        className="d-flex align-items-center gap-2 mb-2"
                        style={{ fontSize: '0.875rem' }}
                      >
                        <ArrowRight size={14} style={{ color: 'hsl(var(--accent))' }} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} className="text-center">
              <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                {language === 'hi' 
                  ? 'अपने संस्थान को डिजिटल बनाएं' 
                  : 'Digitize Your Institution'}
              </h2>
              <p style={{ opacity: 0.9, marginBottom: '2rem' }}>
                {language === 'hi' 
                  ? 'हमारे विशेषज्ञों से संपर्क करें और जानें कि हम आपकी कैसे मदद कर सकते हैं।' 
                  : 'Contact our experts and learn how we can help you.'}
              </p>
              <Link to="/contact" className="btn btn-gov-light d-inline-flex align-items-center gap-2">
                {t('common.getInTouch')}
                <ArrowRight size={18} />
              </Link>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};

export default GovEducation;
